import { useState, useEffect } from 'react';
import { clientsApi, regions } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, MapPin } from 'lucide-react';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    region: 'US',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsApi.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        address: client.address,
        email: client.email || '',
        region: client.region,
      });
    } else {
      setEditingClient(null);
      setFormData({ name: '', address: '', email: '', region: 'US' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, formData);
        toast.success('Client updated');
      } else {
        await clientsApi.create(formData);
        toast.success('Client created');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await clientsApi.delete(id);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getRegionBadgeClass = (region) => {
    const classes = {
      US: 'bg-blue-50 text-blue-700',
      UK: 'bg-purple-50 text-purple-700',
      EU: 'bg-amber-50 text-amber-700',
      IN: 'bg-orange-50 text-orange-700',
      CA: 'bg-red-50 text-red-700',
    };
    return classes[region] || 'bg-zinc-50 text-zinc-700';
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-zinc-900">Clients</h1>
          <p className="text-zinc-500 mt-1">Manage your client list</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-zinc-900 hover:bg-zinc-800"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-zinc-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-zinc-200">
          <Users className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-zinc-700">No clients yet</h3>
          <p className="text-zinc-500 mt-1 mb-4">Add your first client to get started</p>
          <Button onClick={() => handleOpenDialog()} data-testid="add-first-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Client
                </th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Region
                </th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Email
                </th>
                <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr 
                  key={client.id} 
                  className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors"
                  data-testid={`client-row-${client.id}`}
                >
                  <td className="py-4 px-6">
                    <div>
                      <p className="font-medium text-zinc-900">{client.name}</p>
                      <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {client.address}
                      </p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-mono ${getRegionBadgeClass(client.region)}`}>
                      {client.region}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-zinc-600">
                    {client.email || '-'}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(client)}
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(client.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Acme Corporation"
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, Country"
                required
                data-testid="client-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@client.com"
                data-testid="client-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">Region *</Label>
              <Select 
                value={formData.region} 
                onValueChange={(value) => setFormData({ ...formData, region: value })}
              >
                <SelectTrigger data-testid="client-region-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region.value} value={region.value}>
                      {region.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="save-client-btn">
                {editingClient ? 'Update' : 'Add'} Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
